/**
 * Global search-pin store — module-level singleton with subscriptions.
 *
 * Cloud-synced via /api/me/search-pins; surfaces in every SearchBar across
 * all pages and devices for the same logged-in user.
 *
 * State shape:  Array<{ id, query, scope, created_at }>
 *
 * Hooks/SearchBar consumers should call `useGlobalPins()` to read & re-render
 * on changes, and the helper actions to mutate.
 */

import { useEffect, useState } from 'react';
import { api } from './api';

let cache = null;          // null = not loaded; [] = loaded empty
let inflight = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(cache));

const load = async (force = false) => {
  if (inflight && !force) return inflight;
  if (cache !== null && !force) return cache;
  inflight = api
    .get('/me/search-pins')
    .then((r) => {
      cache = Array.isArray(r.data) ? r.data : [];
      notify();
      return cache;
    })
    .catch((err) => {
      // Auth failures or 4xx → return empty silently (search-bar still usable)
      cache = [];
      notify();
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const reloadGlobalPins = () => load(true);

export const addGlobalPin = async (query, scope = 'global') => {
  const q = (query || '').trim();
  if (!q || q.length < 2) return null;
  // Optimistic — only add when API returns the canonical row (with id)
  try {
    const r = await api.post('/me/search-pins', { query: q, scope });
    const pin = r.data;
    if (cache === null) cache = [];
    // De-dupe: replace if same id, else prepend
    const existingIdx = cache.findIndex((p) => p.id === pin.id);
    if (existingIdx >= 0) {
      cache = cache.map((p, i) => (i === existingIdx ? pin : p));
    } else {
      cache = [pin, ...cache];
    }
    notify();
    return pin;
  } catch (err) {
    return null;
  }
};

export const removeGlobalPin = async (pinId) => {
  if (!pinId) return false;
  // Optimistic remove
  const prev = cache;
  if (cache !== null) {
    cache = cache.filter((p) => p.id !== pinId);
    notify();
  }
  try {
    await api.delete(`/me/search-pins/${pinId}`);
    return true;
  } catch (err) {
    // Roll back on failure
    cache = prev;
    notify();
    return false;
  }
};

export const clearGlobalPinsCache = () => {
  cache = null;
  notify();
};

/** React hook — returns current pins array and triggers initial load on mount */
export function useGlobalPins() {
  const [pins, setPins] = useState(cache || []);
  useEffect(() => {
    const handler = (next) => setPins(next || []);
    listeners.add(handler);
    if (cache === null) load();
    else setPins(cache);
    return () => listeners.delete(handler);
  }, []);
  return pins;
}
